import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CrewState, CrewMode, CrewOverrides, StateroomConfig } from "./types";

const initialState: CrewState = {
  shipSize: 100,
  techLevel: 12,
  crewMode: "Commercial",
  crewOverrides: {},
  stateroomConfig: [],
  breakawayHull: undefined,
  loading: false,
  error: null,
};

const crewSlice = createSlice({
  name: "crew",
  initialState,
  reducers: {
    // Ship configuration actions
    setShipSize: (state, action: PayloadAction<number>) => {
      state.shipSize = action.payload;
    },
    setTechLevel: (state, action: PayloadAction<number>) => {
      state.techLevel = action.payload;
    },

    // Crew configuration actions
    setCrewMode: (state, action: PayloadAction<CrewMode>) => {
      state.crewMode = action.payload;
    },
    setCrewOverrides: (state, action: PayloadAction<CrewOverrides>) => {
      state.crewOverrides = action.payload;
    },
    updateCrewOverride: (state, action: PayloadAction<{ role: string; value: number }>) => {
      const { role, value } = action.payload;
      state.crewOverrides[role] = Math.max(0, value); // No negative crew
    },
    resetCrewOverrides: (state) => {
      state.crewOverrides = {};
    },

    // Stateroom configuration actions
    setStateroomConfig: (state, action: PayloadAction<StateroomConfig[]>) => {
      state.stateroomConfig = action.payload;
    },
    addStateroom: (state, action: PayloadAction<StateroomConfig>) => {
      state.stateroomConfig.push(action.payload);
    },
    removeStateroom: (state, action: PayloadAction<number>) => {
      state.stateroomConfig.splice(action.payload, 1);
    },
    updateStateroom: (state, action: PayloadAction<{ index: number; config: StateroomConfig }>) => {
      const { index, config } = action.payload;
      if (index >= 0 && index < state.stateroomConfig.length) {
        state.stateroomConfig[index] = config;
      }
    },

    // Breakaway hull actions
    setBreakawayHull: (state, action: PayloadAction<any>) => {
      state.breakawayHull = action.payload;
    },

    // Utility actions
    reset: (state) => {
      state.crewMode = "Commercial";
      state.crewOverrides = {};
      state.stateroomConfig = [];
      state.breakawayHull = undefined;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setShipSize,
  setTechLevel,
  setCrewMode,
  setCrewOverrides,
  updateCrewOverride,
  resetCrewOverrides,
  setStateroomConfig,
  addStateroom,
  removeStateroom,
  updateStateroom,
  setBreakawayHull,
  reset,
  setLoading,
  setError,
} = crewSlice.actions;

export default crewSlice.reducer;
